/*
 * Jira Ticket:
 * Editd Date: Wed, 18th Mar 2020, 11:58:36 am
 * Author: Jessica Mowatt
 * Email: jessica.mowatt@thedistance.co.uk
 * Copyright (c) 2020 The Distance
 */

import React from 'react';
import {
  Edit,
  FileField,
  FileInput,
  FormDataConsumer,
  ReferenceInput,
  required,
  SelectInput,
  SimpleForm,
  TextInput,
  useTranslate,
  useNotify,
  useRedirect,
  usePermissions,
  Title,
} from 'react-admin';
import { connect } from 'react-redux';
import { useForm } from 'react-final-form';
import { SaveDeleteToolbar } from 'Components';
import { AccessDenied } from 'Layout';
import { columnStyles } from 'styles';
import { platformHeightOptions } from 'utils/choices';
import { onDropRejected } from 'utils';
import { isProjectManager } from 'utils/roles';

const maxFileSize = 2097152; // 2MB in bytes
const allowedFileTypes = 'application/pdf, image/png, image/jpeg';

const SanitizedForm = ({ classes, organisationId }) => {
  const form = useForm();

  const projectFilterChanged = event => {
    form.change('site.id', null);
  };

  return (
    <div className={classes.root}>
      <div className={classes.column}>
        <TextInput
          source="id"
          disabled={true}
          label={'resources.Tower.fields.id'}
        />
        <SelectInput
          source="platformHeight"
          validate={required()}
          choices={platformHeightOptions(20)}
          label={'resources.Tower.fields.height'}
        />
      </div>
      <div className={classes.column}>
        <FormDataConsumer>
          {({ formData }) => (
            <ReferenceInput
              source="site.project.id"
              reference="Project"
              validate={required()}
              onChange={() => projectFilterChanged()}
              filter={{
                organisationId:
                  formData.site && formData.site.project.organisation
                    ? formData.site.project.organisation.id
                    : null,
                principalOrganisationId: organisationId,
              }}
              perPage={1000}
              label={'resources.Tower.fields.project'}
            >
              <SelectInput optionText="name" />
            </ReferenceInput>
          )}
        </FormDataConsumer>
        <TextInput
          source="type.name"
          disabled={true}
          label={'resources.Tower.fields.type'}
        />
      </div>
      <div className={classes.column}>
        <FormDataConsumer>
          {({ formData }) => (
            <ReferenceInput
              source="site.id"
              reference="Site"
              validate={required()}
              perPage={1000}
              disabled={
                formData.site.project && formData.site.project.id ? false : true
              }
              filter={
                formData.site.project && formData.site.project.id
                  ? {
                      projectId: formData.site.project.id,
                    }
                  : null
              }
              label={'resources.Tower.fields.site'}
            >
              <SelectInput optionText="name" />
            </ReferenceInput>
          )}
        </FormDataConsumer>
      </div>
    </div>
  );
};

const TowerEdit = ({ formValues, ...props }) => {
  const { permissions } = usePermissions();
  const translate = useTranslate();
  const classes = columnStyles();
  const notify = useNotify();
  const redirect = useRedirect();
  if (!props.location || !props.location.state) {
    redirect('/');
    notify('error.create.noOrgId', 'warning');
  }
  const { organisationId } = (props.location && props.location.state) || {};
  const divProps = {
    classes,
    organisationId: organisationId?.organisationId,
    ...props,
  };
  const towerRedirect = `/Tower`;

  return isProjectManager(permissions, organisationId) ? (
    <>
      <Title title={`resources.General.cmsTitle`} />
      <Edit
        undoable={false}
        title={`${formValues[props.id] &&
          formValues[props.id].site.project.organisation.name} - ${translate(
          'resources.Tower.fields.towerDetail'
        )}`}
        {...props}
      >
        <SimpleForm
          redirect={towerRedirect}
          toolbar={<SaveDeleteToolbar deleteRedirect={towerRedirect} />}
        >
          <SanitizedForm {...divProps} />
          <TextInput
            source="notes"
            fullWidth={true}
            multiline={true}
            validate={required()}
            label={'resources.Tower.fields.notes'}
          />
          <FileInput
            source="files"
            maxSize={maxFileSize}
            options={{
              onDropRejected: files =>
                onDropRejected({
                  files,
                  translate,
                  notify,
                  maxFileSize,
                  allowedFileTypes,
                }),
            }}
            multiple={true}
            label={'resources.Tower.fields.mediaUpload'}
            accept={allowedFileTypes}
          >
            <FileField source="url" title="filename" target="_blank" />
          </FileInput>
        </SimpleForm>
      </Edit>
    </>
  ) : (
    <AccessDenied />
  );
};

const mapStateToProps = state => ({
  formValues: state.admin.resources.Tower && state.admin.resources.Tower.data,
});
export default connect(mapStateToProps, {})(TowerEdit);
